import React, { useState } from 'react';
import { X, MapPin, Battery, Clock, AlertTriangle, Locate, History, Search, Wrench, Camera } from 'lucide-react';
import { ASSET_STATUSES, ASSET_TYPES } from '../mockData';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import AssetIcon from './AssetIcon';
import CreateTicketModal from './CreateTicketModal';
import { toast } from './ui/sonner';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const AssetDetailPanel = ({ asset, onClose, onStatusUpdate, onReplayPath, onFindSimilar }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState(asset.status);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [assetPhoto, setAssetPhoto] = useState(asset.photo || null);

  // Update selected status when asset prop changes
  React.useEffect(() => {
    setSelectedStatus(asset.status);
  }, [asset.status]);

  if (!asset) return null;

  const assetType = ASSET_TYPES.find(t => t.value === asset.type);
  const statusInfo = ASSET_STATUSES.find(s => s.value === selectedStatus);
  const timeSince = Math.floor((Date.now() - new Date(asset.lastSeen).getTime()) / 60000);

  const handleStatusChange = (newStatus) => {
    setSelectedStatus(newStatus);
    onStatusUpdate(asset.id, newStatus);
    toast.success(t('assetDetail.statusChanged', { status: t(`status.${newStatus.replace('-', '')}`) }));
  };

  const handleShowOnMap = () => {
    navigate('/map', { 
      state: { 
        floor: asset.floor, 
        assetId: asset.id 
      } 
    });
    toast.info(`${asset.name} - ${t('map.floor')} ${asset.floor}`);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAssetPhoto(reader.result);
        // Update asset in localStorage with photo
        const storedAssets = localStorage.getItem('e2-assets');
        if (storedAssets) {
          const assets = JSON.parse(storedAssets);
          const updated = assets.map(a => 
            a.id === asset.id ? { ...a, photo: reader.result } : a
          );
          localStorage.setItem('e2-assets', JSON.stringify(updated));
          toast.success(t('assetDetail.photoUploaded'));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const getBatteryColor = (level) => {
    if (level < 20) return '#F45053';
    if (level < 50) return '#F59E0B';
    return '#10B981';
  };

  const handleCreateTicket = (ticketData) => {
    const newTicket = {
      id: `T${Date.now()}`,
      status: 'open',
      description: ticketData.description,
      createdAt: new Date().toISOString(),
      photo: ticketData.photo
    };
    
    // Update asset with new ticket and change status to out-of-order
    const updatedAsset = {
      ...asset,
      maintenanceTickets: [...(asset.maintenanceTickets || []), newTicket],
      status: 'out-of-order'
    };
    
    onStatusUpdate(asset.id, 'out-of-order', updatedAsset.maintenanceTickets);
    toast.success('Maintenance ticket created successfully');
  };

  const hasOpenTicket = asset.maintenanceTickets?.some(t => t.status === 'open');

  return (
    <div className="asset-detail-panel">
      <div className="panel-header">
        <button className="close-button" onClick={onClose}>
          <X size={20} />
        </button>
      </div>

      <div className="panel-content">
        {/* Asset Icon & Name */}
        <div className="asset-header">
          <div className="asset-icon-large" style={{ borderColor: statusInfo.color }}>
            {assetPhoto ? (
              <img src={assetPhoto} alt={asset.name} className="asset-photo" />
            ) : (
              <AssetIcon type={asset.type} size={28} />
            )}
          </div>
          <div>
            <h2 className="asset-name">{asset.name}</h2>
            <p className="asset-type-label">{t(`assetTypes.${asset.type}`)}</p>
          </div>
        </div>

        {/* Photo Upload */}
        <div className="photo-upload-section">
          <Label htmlFor="asset-photo" className="photo-upload-label">
            <Camera size={16} />
            {assetPhoto ? t('assetDetail.changePhoto') : t('assetDetail.uploadPhoto')}
          </Label>
          <Input
            id="asset-photo"
            type="file"
            accept="image/*"
            onChange={handlePhotoUpload}
            className="photo-input-hidden"
          />
        </div>

        {/* Status Badge */}
        <div className="status-section">
          <label className="section-label">{t('map.status')}</label>
          <Badge 
            className="status-badge-large" 
            style={{ backgroundColor: statusInfo.color }}
          >
            {t(`status.${asset.status.replace(/-/g, '')}`)}
          </Badge>
        </div>

        {/* Location */}
        <div className="info-section">
          <div className="info-row">
            <MapPin size={18} className="info-icon" />
            <div>
              <p className="info-label">{t('assetDetail.lastSeenLocation')}</p>
              <p className="info-value">{t('map.floor')} {asset.floor} - {asset.room}</p>
            </div>
          </div>
        </div>

        {/* Technical Info */}
        <div className="info-section">
          <div className="info-row">
            <Battery size={18} className="info-icon" style={{ color: getBatteryColor(asset.batteryLevel) }} />
            <div>
              <p className="info-label">{t('assetDetail.batteryLevel')}</p>
              <p className="info-value" style={{ color: getBatteryColor(asset.batteryLevel) }}>
                {asset.batteryLevel}%
                {asset.batteryLevel < 20 && ` - ${t('assetDetail.critical')}`}
              </p>
            </div>
          </div>

          <div className="info-row">
            <Clock size={18} className="info-icon" />
            <div>
              <p className="info-label">{t('assetDetail.lastUplink')}</p>
              <p className="info-value">{timeSince} {t('assetDetail.minutesAgo')}</p>
            </div>
          </div>
        </div>

        {/* Maintenance Tickets */}
        {asset.maintenanceTickets && asset.maintenanceTickets.length > 0 && (
          <div className="tickets-section">
            <div className="section-header">
              <AlertTriangle size={18} style={{ color: '#F45053' }} />
              <label className="section-label">{t('assetDetail.maintenanceTickets')}</label>
            </div>
            {asset.maintenanceTickets.map(ticket => (
              <div key={ticket.id} className="ticket-card">
                <div className="ticket-header">
                  <Badge className="ticket-status" style={{ 
                    backgroundColor: ticket.status === 'open' ? '#F45053' : '#F59E0B' 
                  }}>
                    {t(`tickets.${ticket.status === 'in-progress' ? 'inProgress' : ticket.status}`)}
                  </Badge>
                  <span className="ticket-date">
                    {new Date(ticket.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <p className="ticket-description">{ticket.description}</p>
              </div>
            ))}
          </div>
        )}

        {/* Status Update */}
        <div className="status-update-section">
          <label className="section-label">{t('assetDetail.updateStatus')}</label>
          <div className="status-buttons-grid">
            {ASSET_STATUSES.map(status => (
              <button
                key={status.value}
                onClick={() => handleStatusChange(status.value)}
                className={`status-button ${selectedStatus === status.value ? 'active' : ''}`}
                style={{ 
                  '--status-color': status.color,
                  backgroundColor: selectedStatus === status.value ? status.color : 'transparent',
                  borderColor: status.color,
                  color: selectedStatus === status.value ? '#ffffff' : status.color
                }}
              >
                <span className="status-dot" style={{ 
                  backgroundColor: selectedStatus === status.value ? '#ffffff' : status.color 
                }} />
                {t(`status.${status.value.replace(/-/g, '')}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <Button 
            className="action-button" 
            onClick={handleShowOnMap}
          >
            <Locate size={18} />
            {t('assetDetail.showOnMap')}
          </Button>
          
          <Button 
            className="action-button" 
            variant="outline"
            onClick={() => onReplayPath(asset)}
          >
            <History size={18} />
            {t('assetDetail.replayLast2Hours')}
          </Button>
          
          <Button 
            className="action-button" 
            variant="outline"
            onClick={() => onFindSimilar(asset)}
          >
            <Search size={18} />
            {t('assetDetail.findSimilarNearby')}
          </Button>

          <Button 
            className="action-button action-button-ticket" 
            variant="outline"
            onClick={() => setShowTicketModal(true)}
          >
            <Wrench size={18} />
            {t('assetDetail.createTicket')}
          </Button>
        </div>
      </div>

      {/* Create Ticket Modal */}
      <CreateTicketModal
        isOpen={showTicketModal}
        onClose={() => setShowTicketModal(false)}
        asset={asset}
        onCreateTicket={handleCreateTicket}
        hasOpenTicket={hasOpenTicket}
      />
    </div>
  );
};

export default AssetDetailPanel;
